import { api } from "../api";

export interface ProductCombinationValueInput {
  variationDefinitionId: string;
  variationValueId?: string | null;
  valueSnapshot: string;
  labelSnapshot: string;
  /** Typed by the store owner on this product; the backend creates it on save. */
  isNewCustomValue?: boolean;
}

export interface ProductCombinationInput {
  /**
   * "Vender" toggle. Sent as `isActive`: a row switched off is still saved,
   * as not sold, so it comes back switched off when the product is edited.
   * The backend only generates the internal SKU the first time a
   * combination is put on sale. Missing = on.
   */
  sell?: boolean;
  /** Sent to the API: false saves the combination as not sold. */
  isActive?: boolean;
  id?: string;
  internalSku?: string;
  sellerSku?: string | null;
  price: number;
  stock: number;
  values: ProductCombinationValueInput[];
}

export interface ProductCombinationResponse extends ProductCombinationInput {
  id: string;
  internalSku: string;
  isActive: boolean;
}

export interface CombinationOption {
  variationDefinitionId: string;
  variationLabel: string;
  variationValueId?: string | null;
  value: string;
  label: string;
  /** Saved custom value of the store owner. */
  isCustom?: boolean;
  /** Unit of a numeric variation, shown after the label ("10,50 cm"). */
  unit?: string | null;
  /** Typed on this product and not saved yet: still editable and removable. */
  isNewCustom?: boolean;
}

export { toVariationValueKey } from "shared/utils/variationValue";

export function generateCombinations(
  optionsByVariation: CombinationOption[][],
): ProductCombinationInput[] {
  if (
    optionsByVariation.length === 0 ||
    optionsByVariation.some((options) => options.length === 0)
  ) {
    return [];
  }

  return optionsByVariation.reduce<ProductCombinationInput[]>(
    (combinations, options) => {
      if (combinations.length === 0) {
        return options.map((option) => ({
          price: 0,
          stock: 0,
          values: [toValueInput(option)],
        }));
      }

      return combinations.flatMap((combination) =>
        options.map((option) => ({
          price: combination.price,
          stock: combination.stock,
          values: [...combination.values, toValueInput(option)],
        })),
      );
    },
    [],
  );
}

export async function listarCombinacoes(
  productId: string,
  includeInactive = false,
): Promise<ProductCombinationResponse[]> {
  const response = await api.get<ProductCombinationResponse[]>(
    `/v2/products/${productId}/combinations`,
    { params: includeInactive ? { includeInactive: true } : undefined },
  );
  return response.data;
}

export async function sincronizarCombinacoes(
  productId: string,
  combinations: ProductCombinationInput[],
): Promise<ProductCombinationResponse[]> {
  const response = await api.put<ProductCombinationResponse[]>(
    `/v2/products/${productId}/combinations`,
    { combinations: toCombinationPayload(combinations) },
  );
  return response.data;
}

export function applyGlobalCombinationValues(
  combinations: ProductCombinationInput[],
  price: number,
  stock: number,
): ProductCombinationInput[] {
  return combinations.map((combination) => ({
    ...combination,
    price,
    stock,
  }));
}

function combinationKey(values: ProductCombinationValueInput[]): string {
  return values
    .map(
      (value) =>
        `${value.variationDefinitionId}:${value.variationValueId ?? value.valueSnapshot}`,
    )
    .sort()
    .join("|");
}

/**
 * Keeps id, seller SKU, price and stock of rows whose value set already
 * existed, so regenerating the matrix does not recreate saved combinations.
 * A row matching a combination removed earlier (inactive) gets its seller SKU
 * and price back, but not its stock: the backend reactivates that combination
 * on save, and the old stock is unlikely to still be true.
 */
export function mergeCombinations(
  generated: ProductCombinationInput[],
  existing: ProductCombinationInput[],
  inactive: ProductCombinationInput[] = [],
): ProductCombinationInput[] {
  const existingByKey = new Map(
    existing.map((combination) => [
      combinationKey(combination.values),
      combination,
    ]),
  );
  // Later entries win: the API lists by internal SKU, so the most recent one.
  const inactiveByKey = new Map(
    inactive.map((combination) => [
      combinationKey(combination.values),
      combination,
    ]),
  );

  return generated.map((combination) => {
    const key = combinationKey(combination.values);
    const previous = existingByKey.get(key);
    if (previous) {
      return {
        ...combination,
        id: previous.id,
        internalSku: previous.internalSku,
        sellerSku: previous.sellerSku,
        price: previous.price,
        stock: previous.stock,
        sell: previous.sell,
      };
    }

    // A combination not sold today (switched off, or its value was
    // unselected) comes back off: the owner decides whether to sell it again.
    const removed = inactiveByKey.get(key);
    return removed
      ? {
          ...combination,
          internalSku: removed.internalSku,
          sellerSku: removed.sellerSku,
          price: removed.price,
          stock: 0,
          sell: false,
        }
      : combination;
  });
}

/** Rebuilds the per-variation selection from saved combinations. */
export function toSelectedVariationOptions(
  combinations: ProductCombinationInput[],
): Record<string, CombinationOption[]> {
  const selected: Record<string, CombinationOption[]> = {};

  for (const combination of combinations) {
    for (const value of combination.values) {
      const options = selected[value.variationDefinitionId] ?? [];
      selected[value.variationDefinitionId] = options;
      const optionId = value.variationValueId ?? value.valueSnapshot;
      if (
        options.some(
          (option) => (option.variationValueId ?? option.value) === optionId,
        )
      ) {
        continue;
      }
      options.push({
        variationDefinitionId: value.variationDefinitionId,
        variationLabel: "",
        variationValueId: value.variationValueId ?? null,
        value: value.valueSnapshot,
        label: value.labelSnapshot,
      });
    }
  }

  return selected;
}

export function toCombinationPayload(
  combinations: ProductCombinationInput[],
): ProductCombinationInput[] {
  return combinations.map((combination) => ({
    isActive: combination.sell !== false,
    id: combination.id,
    sellerSku: combination.sellerSku ?? null,
    price: combination.price,
    stock: combination.stock,
    values: combination.values.map((value) => ({
      variationDefinitionId: value.variationDefinitionId,
      variationValueId: value.variationValueId ?? null,
      valueSnapshot: value.valueSnapshot,
      labelSnapshot: value.labelSnapshot,
      ...(value.isNewCustomValue ? { isNewCustomValue: true } : {}),
    })),
  }));
}

function toValueInput(option: CombinationOption): ProductCombinationValueInput {
  return {
    variationDefinitionId: option.variationDefinitionId,
    variationValueId: option.variationValueId ?? null,
    valueSnapshot: option.value,
    labelSnapshot: option.label,
    ...(option.isNewCustom ? { isNewCustomValue: true } : {}),
  };
}
